// Eleven more widgets for custom dashboards, each with styles (the first is the default): average
// win vs average loss, accounts, month over month, what your mistakes cost, discipline score,
// trading after a loss, monthly P&L, compact calendar heatmap, P&L by setup, balance & growth, and
// tool costs vs profit (courses, data, platforms; prop firm fees stay in My Trading Journey).
import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { accountStatus } from './balance';
import { isPropAccount } from './DashboardOverview';
import InfoTip from './InfoTip';
import { detectBehavior } from './behaviorDetect';
import { currentTradingDay, netPnl, tradingDayKey, usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

export const NEW_STYLES = {
  avgWinLoss: [['a', 'Bars'], ['b', 'Ring'], ['c', 'Split']],
  accountsList: [['a', 'List'], ['b', 'Tiles']],
  mom: [['a', 'Table'], ['b', 'List'], ['c', 'Ring']],
  mistakes: [['a', 'Bars'], ['b', 'Tiles'], ['c', 'Costliest']],
  discipline: [['a', 'Ring'], ['b', 'Number'], ['c', 'Days']],
  afterLoss: [['a', 'Bars'], ['b', 'Side by side']],
  monthly: [['a', 'Columns'], ['b', 'Rows']],
  heatmap: [['b', 'Big'], ['a', 'Small']],
  setups: [['a', 'Table'], ['b', 'Bars']],
  growth: [['a', 'Steps'], ['b', 'List']],
  toolCosts: [['a', 'Summary'], ['b', 'By category']],
};

const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');
const money = (n) => `${n < 0 ? '−' : ''}${usd.format(Math.abs(n || 0))}`;
const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd.format(Math.abs(n || 0))}`;
const short = (n) => (Math.abs(n) >= 1000 ? `${n > 0 ? '+' : n < 0 ? '−' : ''}$${(Math.abs(n) / 1000).toFixed(1)}K` : `${n > 0 ? '+' : n < 0 ? '−' : ''}$${Math.round(Math.abs(n))}`);
const monthName = (m, year) => new Date(`${m}-15T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', ...(year ? { year: '2-digit' } : {}) });
const C = 2 * Math.PI * 34;

function Card({ title, info, extra, children }) {
  const { t } = useI18n();
  return (
    <section className="sw">
      <div className="sw-head"><span className="sw-title">{t(title)}{info && <InfoTip label={t(title)} text={t(info)} />}</span>{extra}</div>
      {children}
    </section>
  );
}
function Chips({ items }) {
  return <span className="sw-chips">{items.filter(Boolean).map(([text, kind]) => <span key={text} className={`sw-chip ${kind || ''}`}>{text}</span>)}</span>;
}
function Seg({ options, value, onChange }) {
  const { t } = useI18n();
  return <span className="sw-seg" role="group">{options.map(([id, label]) => <button key={id} type="button" className={value === id ? 'on' : ''} aria-pressed={value === id} onClick={() => onChange(id)}>{t(label)}</button>)}</span>;
}
function Ring({ pct, kind, label, sub }) {
  return (
    <svg className="sw-ring" viewBox="0 0 84 84" aria-hidden="true">
      <circle cx="42" cy="42" r="34" className="tr" />
      <circle cx="42" cy="42" r="34" className={`on ${kind}`} strokeDasharray={`${Math.max(0, Math.min(1, pct / 100)) * C} ${C}`} transform="rotate(-90 42 42)" />
      <text x="42" y={sub ? 45 : 48}>{label}</text>{sub && <text x="42" y="57" className="s">{sub}</text>}
    </svg>
  );
}
function Empty({ text }) {
  const { t } = useI18n();
  return <p className="sw-empty">{t(text)}</p>;
}
function stats(list) {
  const nets = list.map(netPnl);
  const w = nets.filter((n) => n > 0);
  const l = nets.filter((n) => n < 0).map((n) => -n);
  const gw = w.reduce((s, n) => s + n, 0);
  const gl = l.reduce((s, n) => s + n, 0);
  return { n: list.length, net: gw - gl, wins: w.length, losses: l.length, wr: list.length ? (w.length / list.length) * 100 : 0,
    pf: gl ? gw / gl : null, aw: w.length ? gw / w.length : 0, al: l.length ? gl / l.length : 0 };
}
function since(trades, period) {
  if (period === 'all') return trades;
  const from = new Date(`${currentTradingDay()}T12:00:00Z`);
  from.setUTCDate(from.getUTCDate() - (period === '30d' ? 29 : 89));
  const key = from.toISOString().slice(0, 10);
  return trades.filter((x) => tradingDayKey(x.date) >= key);
}
const PERIODS3 = [['30d', '30D'], ['90d', '90D'], ['all', 'All']];

// 1. Average win vs average loss --------------------------------------------------------------
function AvgWinLoss({ trades, style }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState('90d');
  const s = stats(since(trades, period));
  const info = 'Your average winning trade against your average losing trade. The bigger your wins are compared with your losses, the lower the win rate you need to make money.';
  const seg = <Seg options={PERIODS3} value={period} onChange={setPeriod} />;
  if (!s.wins || !s.losses) return <Card title="Average win vs average loss" info={info} extra={seg}><Empty text="Needs a win and a loss." /></Card>;
  const r = s.aw / s.al;
  const be = 100 / (1 + r);
  const top = Math.max(s.aw, s.al);
  if (style === 'b') {
    return (
      <Card title="Payoff ratio" info={info} extra={seg}>
        <div className="sw-row nowrap gap"><Ring pct={Math.min(100, (r / 2) * 100)} kind={r >= 1 ? 'g' : 'r'} label={`${r.toFixed(2)}×`} sub={t('payoff')} />
          <div className="sw-stack"><span className="sw-kv"><span>{t('Average win')}</span><b className="gain">{money(s.aw)}</b></span><span className="sw-kv"><span>{t('Average loss')}</span><b className="loss">{money(-s.al)}</b></span>
            <Chips items={[[t(r >= 1 ? 'Wins bigger than losses' : 'Wins smaller than losses'), r >= 1 ? 'g' : 'r']]} /></div></div>
        <span className="sw-foot">{t('Break-even win rate at this payoff: {p}%.', { p: Math.round(be) })}</span>
      </Card>
    );
  }
  if (style === 'c') {
    return (
      <Card title="Average win vs average loss" info={info} extra={seg}>
        <div className="nw-duo"><span><small>{t('Average win')}</small><b className="sw-big2 gain">{money(s.aw)}</b></span><span className="nw-vs">{t('vs')}</span><span><small>{t('Average loss')}</small><b className="sw-big2 loss">{money(-s.al)}</b></span></div>
        <div className="sw-wl"><b style={{ flex: s.aw }} /><i style={{ flex: s.al }} /></div>
        <span className="sw-foot">{t('{x}× · {n} trades', { x: r.toFixed(2), n: s.n })}</span>
      </Card>
    );
  }
  return (
    <Card title="Average win vs average loss" info={info} extra={seg}>
      <div className={`sw-big ${r >= 1 ? 'gain' : 'loss'}`}>{r.toFixed(2)}×</div>
      <span className="sw-muted">{t('your average win is {x} times your average loss', { x: r.toFixed(2) })}</span>
      <div className="sw-cmp">
        <span><small>{t('Average win')}</small><b className="gain">{money(s.aw)}</b><span className="sw-bar"><i className="g" style={{ width: `${(s.aw / top) * 100}%` }} /></span></span>
        <span><small>{t('Average loss')}</small><b className="loss">{money(-s.al)}</b><span className="sw-bar"><i className="r" style={{ width: `${(s.al / top) * 100}%` }} /></span></span>
      </div>
      <span className="sw-foot">{t('At {x}× you need to win {be}% of trades to break even. You win {wr}%.', { x: r.toFixed(2), be: Math.round(be), wr: Math.round(s.wr) })}</span>
    </Card>
  );
}

// 2. Accounts -----------------------------------------------------------------------------------
function AccountsList({ ctx, style }) {
  const { t } = useI18n();
  const { accounts, allTrades, shownIds } = ctx;
  const rows = accounts.filter((a) => !a.archived && (!shownIds || shownIds.includes(a._id))).map((a) => {
    const mine = allTrades.filter((x) => String(x.accountId) === String(a._id));
    const days = {};
    for (const x of mine) days[tradingDayKey(x.date)] = (days[tradingDayKey(x.date)] || 0) + netPnl(x);
    const keys = Object.keys(days).sort();
    const last = keys.length ? days[keys[keys.length - 1]] : 0;
    const status = a.size ? accountStatus(a, mine) : null;
    const balance = status ? status.balance : mine.reduce((s, x) => s + netPnl(x), 0);
    const prop = isPropAccount(a);
    const level = !prop || !status || status.toBlow === undefined ? null : status.risk === 'danger' || status.risk === 'blown' ? ['At risk', 'r'] : status.risk === 'warning' ? ['Caution', 'y'] : ['Safe', 'g'];
    return { a, balance, last, level, prop, hasBalance: Boolean(status) };
  });
  const info = 'Every account with its balance, its last trading day and, for accounts with a max loss, how much room is left: Safe, Caution or At risk.';
  if (!rows.length) return <Card title="Accounts" info={info}><Empty text="No accounts yet." /></Card>;
  const dot = (r) => <i className={`dov-dot ${r.prop ? 'prop' : 'personal'}`} aria-hidden="true" />;
  const badge = (r) => <Chips items={[r.level ? [t(r.level[0]), r.level[1]] : [t(r.prop ? 'Prop' : 'Personal'), '']]} />;
  if (style === 'b') {
    return (
      <Card title="Accounts" info={info}>
        <div className="nw-agrid">{rows.map((r) => (
          <div key={r.a._id} className="nw-atile"><span className="nw-dotn">{dot(r)}<b>{r.a.name}</b></span><b className="sw-big2">{r.hasBalance ? money(r.balance) : signed(r.balance)}</b>
            <span className={`nw-mono ${tone(r.last)}`}>{t('{amount} last day', { amount: signed(r.last) })}</span>{badge(r)}</div>
        ))}</div>
      </Card>
    );
  }
  return (
    <Card title="Accounts" info={info}>
      <ul className="nw-alist">{rows.map((r) => (
        <li key={r.a._id}><span className="nw-dotn">{dot(r)}<b>{r.a.name}</b></span><span className="nw-mono">{r.hasBalance ? money(r.balance) : signed(r.balance)}</span><span className={`nw-mono ${tone(r.last)}`}>{signed(r.last)}</span>{badge(r)}</li>
      ))}</ul>
      <span className="sw-foot">{t('{n} accounts · last day = each account’s latest trading day', { n: rows.length })}</span>
    </Card>
  );
}

// 3. Month over month ---------------------------------------------------------------------------
function MonthOverMonth({ trades, style }) {
  const { t } = useI18n();
  const months = [...new Set(trades.map((x) => tradingDayKey(x.date).slice(0, 7)))].sort();
  const info = 'Your latest month with trades next to the month before, on the numbers that matter. Green means better, red means worse.';
  if (months.length < 2) return <Card title="Month over month" info={info}><Empty text="Needs two months of trades." /></Card>;
  const [pm, cm] = months.slice(-2);
  const a = stats(trades.filter((x) => tradingDayKey(x.date).startsWith(pm)));
  const b = stats(trades.filter((x) => tradingDayKey(x.date).startsWith(cm)));
  const chg = (better, text, same) => (same ? <span className="nw-chg same">= {t('same')}</span> : <span className={`nw-chg ${better ? 'up' : 'dn'}`}>{better ? '▲' : '▼'} {text}</span>);
  const rows = [
    [t('Net P&L'), signed(a.net), signed(b.net), chg(b.net > a.net, short(b.net - a.net), Math.round(b.net) === Math.round(a.net))],
    [t('Win rate'), `${Math.round(a.wr)}%`, `${Math.round(b.wr)}%`, chg(b.wr > a.wr, t('{n} pts', { n: `${b.wr - a.wr > 0 ? '+' : ''}${Math.round(b.wr - a.wr)}` }), Math.round(b.wr) === Math.round(a.wr))],
    [t('Profit factor'), a.pf === null ? '—' : a.pf.toFixed(2), b.pf === null ? '—' : b.pf.toFixed(2), chg((b.pf || 0) > (a.pf || 0), `${((b.pf || 0) - (a.pf || 0)) > 0 ? '+' : ''}${((b.pf || 0) - (a.pf || 0)).toFixed(2)}`, (a.pf || 0).toFixed(2) === (b.pf || 0).toFixed(2))],
    [t('Average loss'), money(-a.al), money(-b.al), chg(b.al < a.al, t(b.al < a.al ? '{amount} smaller' : '{amount} bigger', { amount: `$${Math.round(Math.abs(b.al - a.al))}` }), Math.round(a.al) === Math.round(b.al))],
    [t('Trades'), String(a.n), String(b.n), chg(b.n <= a.n, t(b.n <= a.n ? '{n} fewer' : '{n} more', { n: Math.abs(b.n - a.n) }), a.n === b.n)],
  ];
  const better = rows.filter((r) => r[3].props.className === 'nw-chg up').length;
  const foot = t('{n} of 5 key numbers improved.', { n: better });
  if (style === 'b') {
    return (
      <Card title="This month vs last" info={info}>
        {rows.map((r) => <div key={r[0]} className="nw-mrow"><span>{r[0]}</span><b className="nw-mono">{r[2]}</b>{r[3]}</div>)}
        <span className="sw-foot">{t('{n} of 5 better than {month}.', { n: better, month: monthName(pm) })}</span>
      </Card>
    );
  }
  if (style === 'c') {
    return (
      <Card title="Month over month" info={info}>
        <div className="sw-row nowrap gap"><Ring pct={(better / 5) * 100} kind={better >= 3 ? 'g' : 'r'} label={`${better}/5`} sub={t('better')} />
          <div className="sw-stack"><b>{t('{n} of 5 numbers improved', { n: better })}</b>{rows.slice(0, 4).map((r) => <span key={r[0]} className="sw-kv"><span>{r[0]}</span>{r[3]}</span>)}</div></div>
      </Card>
    );
  }
  return (
    <Card title="Month over month" info={info}>
      <table className="nw-tbl"><thead><tr><th /><th>{monthName(pm)}</th><th>{monthName(cm)}</th><th>{t('Change')}</th></tr></thead>
        <tbody>{rows.map((r) => <tr key={r[0]}><td>{r[0]}</td><td className="nw-mono sw-muted">{r[1]}</td><td className="nw-mono"><b>{r[2]}</b></td><td>{r[3]}</td></tr>)}</tbody></table>
      <span className="sw-foot">{foot}</span>
    </Card>
  );
}

// 4. What your mistakes cost --------------------------------------------------------------------
function MistakesCost({ trades, library = [], style }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState('90d');
  const mistakeTags = new Set(library.filter((x) => x.kind === 'mistake').map((x) => x.name.toLowerCase()));
  const list = since(trades, period);
  const by = {};
  for (const x of list) for (const tag of x.tags || []) {
    if (!mistakeTags.has(tag.toLowerCase())) continue;
    (by[tag] = by[tag] || { n: 0, net: 0 }).n += 1;
    by[tag].net += netPnl(x);
  }
  const rows = Object.entries(by).sort((p, q) => p[1].net - q[1].net);
  const info = 'Every trade you tagged with a mistake tag (like Revenge trade or Moved stop), what those trades made or lost, worst first.';
  const seg = <Seg options={PERIODS3} value={period} onChange={setPeriod} />;
  if (!rows.length) return <Card title="What your mistakes cost" info={info} extra={seg}><Empty text="No trades tagged with a mistake in this period. Tag a trade with a mistake (like Revenge trade) and its cost shows here." /></Card>;
  const total = rows.reduce((s, [, v]) => s + v.net, 0);
  const count = rows.reduce((s, [, v]) => s + v.n, 0);
  const top = Math.max(...rows.map(([, v]) => Math.abs(v.net)));
  const without = stats(list).net - total;
  if (style === 'b') {
    return (
      <Card title="Mistakes" info={info} extra={seg}>
        <div className="nw-mgrid">{rows.slice(0, 6).map(([n, v], i) => <span key={n} className={i === 0 && v.net < 0 ? 'hot' : ''}><small>{n}</small><b className={tone(v.net)}>{short(v.net)}</b><em>{t(v.n === 1 ? '{n} trade' : '{n} trades', { n: v.n })}</em></span>)}</div>
        <span className="sw-foot">{t('Your costliest: {name}.', { name: rows[0][0] })}</span>
      </Card>
    );
  }
  if (style === 'c') {
    const [name, v] = rows[0];
    return (
      <Card title="Your costliest mistake" info={info} extra={seg}>
        <div className="sw-big2">{name}</div><div className={`sw-big ${tone(v.net)}`}>{signed(v.net)}</div>
        <span className="sw-muted">{t('{n} trades · {each} each', { n: v.n, each: signed(v.net / v.n) })}</span>
        {rows.slice(1, 4).map(([n, x]) => <div key={n} className="sw-kv"><span>{n}</span><b className={tone(x.net)}>{signed(x.net)}</b></div>)}
      </Card>
    );
  }
  return (
    <Card title="What your mistakes cost" info={info} extra={seg}>
      <div className={`sw-big ${tone(total)}`}>{signed(total)}</div>
      <span className="sw-muted">{t('from {n} trades tagged as mistakes', { n: count })}</span>
      <ul className="nw-mlist">{rows.map(([n, v]) => (
        <li key={n}><span>{n} <small>×{v.n}</small></span><span className="tk"><i className={v.net < 0 ? 'loss' : 'gain'} style={{ width: `${(Math.abs(v.net) / top) * 100}%`, left: 0 }} /></span><b className={tone(v.net)}>{signed(v.net)}</b></li>
      ))}</ul>
      <span className="sw-foot">{t('Without these, this period would be {amount}.', { amount: signed(without) })}</span>
    </Card>
  );
}

// 5. Discipline score ---------------------------------------------------------------------------
let routineCache = null;
function useRules() {
  const [rules, setRules] = useState(routineCache);
  useEffect(() => {
    let live = true;
    api('/routine').then((r) => { routineCache = (r && r.rules) || []; if (live) setRules(routineCache); }).catch(() => live && setRules([]));
    return () => { live = false; };
  }, []);
  return rules;
}
function Discipline({ trades, notes = {}, style }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState('month');
  const rules = useRules();
  const info = 'How often you followed your Morning prep rules on the days you traded, rule by rule, and how many revenge trades you took.';
  const seg = <Seg options={[['week', 'Week'], ['month', 'Month']]} value={period} onChange={setPeriod} />;
  if (rules === null) return <Card title="Discipline score" info={info} extra={seg}><span className="dw-skeleton" /></Card>;
  if (!rules.length) return <Card title="Discipline score" info={info} extra={seg}><Empty text="Write your trading rules in Morning prep and tick them each day to get a discipline score." /></Card>;
  const today = currentTradingDay();
  const from = new Date(`${today}T12:00:00Z`);
  from.setUTCDate(from.getUTCDate() - (period === 'week' ? 6 : 29));
  const fromKey = from.toISOString().slice(0, 10);
  const inRange = trades.filter((x) => tradingDayKey(x.date) >= fromKey);
  const days = [...new Set(inRange.map((x) => tradingDayKey(x.date)))].sort();
  if (!days.length) return <Card title="Discipline score" info={info} extra={seg}><Empty text="No trading days in this period." /></Card>;
  const followed = (d) => (notes[d] && notes[d].rulesFollowed) || [];
  const per = rules.map((r) => [r, (days.filter((d) => followed(d).includes(r)).length / days.length) * 100]);
  const score = per.reduce((s, [, p]) => s + p, 0) / per.length;
  const clean = days.filter((d) => rules.every((r) => followed(d).includes(r))).length;
  const revenge = detectBehavior(inRange).patterns.find((p) => p.kind === 'revenge');
  const revCount = revenge ? revenge.count : 0;
  const col = (p) => (p >= 85 ? 'g' : p >= 65 ? 'y' : 'r');
  const cls = (p) => (p >= 85 ? 'gain' : p >= 65 ? 'gold' : 'loss');
  if (style === 'b') {
    return (
      <Card title="Discipline score" info={info} extra={seg}>
        <div className={`sw-big ${cls(score)}`}>{Math.round(score)}<small className="sw-muted"> / 100</small></div>
        <span className="sw-muted">{t('rules followed on the days you traded')}</span>
        {per.map(([r, p]) => <div key={r} className="sw-kv"><span className="nw-clip">{r}</span><b>{Math.round(p)}%</b></div>)}
        <span className="sw-foot">{t('{clean} fully clean days · {n} revenge trades', { clean, n: revCount })}</span>
      </Card>
    );
  }
  if (style === 'c') {
    return (
      <Card title="Rules on your trading days" info={info} extra={seg}>
        <div className="nw-rdays">{days.map((d) => <i key={d} className={followed(d).length >= rules.length ? 'g' : followed(d).length ? 'y' : 'r'} title={d} />)}</div>
        <div className="sw-row between"><b>{t('{a} of {b} days', { a: clean, b: days.length })}</b><span className="sw-muted">{t('every rule followed')}</span></div>
        <span className="sw-foot">{t('Green: all {n} rules · yellow: some · red: none ticked', { n: rules.length })}</span>
      </Card>
    );
  }
  return (
    <Card title="Discipline score" info={info} extra={seg}>
      <div className="sw-row nowrap gap"><Ring pct={score} kind={col(score)} label={`${Math.round(score)}`} sub="/ 100" />
        <div className="sw-stack wide">{per.map(([r, p]) => <span key={r} className="nw-drow"><span className="nw-clip">{r}</span><span className="sw-bar"><i className={col(p)} style={{ width: `${p}%` }} /></span><b>{Math.round(p)}%</b></span>)}</div></div>
      <Chips items={[revCount > 0 ? [t(revCount === 1 ? '{n} revenge trade' : '{n} revenge trades', { n: revCount }), 'r'] : [t('No revenge trades'), 'g']]} />
      <span className="sw-foot">{t('{a} of {b} trading days with every rule followed.', { a: clean, b: days.length })}</span>
    </Card>
  );
}

// 6. Trading after a loss -----------------------------------------------------------------------
function AfterLoss({ trades, style }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState('90d');
  const list = [...since(trades, period)].sort((a, b) => new Date(a.date) - new Date(b.date));
  const after = list.filter((x, i) => i > 0 && tradingDayKey(list[i - 1].date) === tradingDayKey(x.date) && netPnl(list[i - 1]) < 0);
  const sa = stats(after);
  const so = stats(list);
  const info = 'Your win rate on the trade right after a losing trade (the same day), next to your win rate overall. A big gap is a sign of tilt.';
  const seg = <Seg options={[['30d', '30D'], ['90d', '90D']]} value={period} onChange={setPeriod} />;
  if (after.length < 3) return <Card title="Trading after a loss" info={info} extra={seg}><Empty text="Needs a few trades taken right after a loss." /></Card>;
  const worse = sa.wr < so.wr;
  if (style === 'b') {
    return (
      <Card title="After a loss" info={info} extra={seg}>
        <div className="nw-duo"><span><small>{t('After a loss')}</small><b className={`sw-big2 ${worse ? 'loss' : 'gain'}`}>{Math.round(sa.wr)}%</b></span><span className="nw-vs">{t('vs')}</span><span><small>{t('Overall')}</small><b className="sw-big2">{Math.round(so.wr)}%</b></span></div>
        <span className="sw-foot">{t(worse ? '{n} points worse right after a loss · those {k} trades made {amount}.' : '{n} points better right after a loss · those {k} trades made {amount}.', { n: Math.abs(Math.round(so.wr - sa.wr)), k: sa.n, amount: signed(sa.net) })}</span>
      </Card>
    );
  }
  return (
    <Card title="Trading after a loss" info={info} extra={seg}>
      <div className={`sw-big ${worse ? 'loss' : 'gain'}`}>{Math.round(sa.wr)}%</div>
      <span className="sw-muted">{t('win rate on the trade right after a loss')}</span>
      <div className="sw-cmp">
        <span><small>{t('Next trade after a loss')}</small><b>{Math.round(sa.wr)}%</b><span className="sw-bar"><i className={worse ? 'r' : 'g'} style={{ width: `${sa.wr}%` }} /></span></span>
        <span><small>{t('All your trades')}</small><b>{Math.round(so.wr)}%</b><span className="sw-bar"><i className="c" style={{ width: `${so.wr}%` }} /></span></span>
      </div>
      <Chips items={[[t('{amount} over {n} trades', { amount: signed(sa.net), n: sa.n }), sa.net < 0 ? 'r' : 'g']]} />
      {worse && <span className="sw-foot">{t('Try a 15-minute break after every loss.')}</span>}
    </Card>
  );
}

// 7. Monthly P&L ------------------------------------------------------------------------------
function Monthly({ trades, style }) {
  const { t } = useI18n();
  const by = {};
  for (const x of trades) { const m = tradingDayKey(x.date).slice(0, 7); by[m] = (by[m] || 0) + netPnl(x); }
  const rows = Object.entries(by).sort().slice(-12);
  const info = 'Your net P&L for each of your last 12 months with trades.';
  if (!rows.length) return <Card title="Monthly P&L" info={info}><Empty text="No trades yet." /></Card>;
  const top = Math.max(1, ...rows.map(([, v]) => Math.abs(v)));
  const green = rows.filter(([, v]) => v > 0).length;
  if (style === 'b') {
    return (
      <Card title="Monthly P&L" info={info}>
        <ul className="sw-hsplit">{rows.map(([m, v]) => <li key={m}><span>{monthName(m, true)}</span><span className="tk"><i className={tone(v)} style={{ width: `${(Math.abs(v) / top) * 50}%`, [v < 0 ? 'right' : 'left']: '50%' }} /></span><b className={tone(v)}>{short(v)}</b></li>)}</ul>
      </Card>
    );
  }
  return (
    <Card title="Monthly P&L" info={info} extra={<Chips items={[[t('{n} green', { n: green }), 'g'], [t('{n} red', { n: rows.length - green }), 'r']]} />}>
      <div className="sw-vbars">{rows.map(([m, v]) => (
        <span key={m} className="vb" title={`${monthName(m, true)} ${signed(v)}`}>
          <span className="up">{v > 0 && <i className="g" style={{ height: `${(v / top) * 100}%` }} />}</span>
          <span className="dn">{v < 0 && <i className="r" style={{ height: `${(-v / top) * 100}%` }} />}</span>
          <small>{monthName(m)}</small>
        </span>
      ))}</div>
      <span className="sw-foot">{t('Net {amount} over these months.', { amount: signed(rows.reduce((s, [, v]) => s + v, 0)) })}</span>
    </Card>
  );
}

// 8. Compact calendar heatmap -------------------------------------------------------------------
function Heatmap({ trades, style }) {
  const { t } = useI18n();
  const keys = trades.map((x) => tradingDayKey(x.date)).sort();
  const month = (keys[keys.length - 1] || currentTradingDay()).slice(0, 7);
  const days = {};
  for (const x of trades) { const k = tradingDayKey(x.date); if (k.startsWith(month)) days[k] = (days[k] || 0) + netPnl(x); }
  const top = Math.max(1, ...Object.values(days).map(Math.abs));
  const [y, m] = month.split('-').map(Number);
  const cells = [];
  const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  for (let i = 0; i < Math.max(0, first - 1) && first !== 0 && first !== 6; i += 1) cells.push(null);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  for (let d = 1; d <= last; d += 1) {
    const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    if (wd === 0 || wd === 6) continue;
    cells.push(`${month}-${String(d).padStart(2, '0')}`);
  }
  const net = Object.values(days).reduce((s, v) => s + v, 0);
  const green = Object.values(days).filter((v) => v > 0).length;
  const info = 'Your latest month, one square per weekday: green for a winning day, red for a losing one, darker for bigger.';
  const title = new Date(`${month}-15T12:00:00`).toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' });
  return (
    <Card title={title} info={info}>
      <div className={`nw-heat${style === 'a' ? '' : ' big'}`}>
        {[t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri')].map((d) => <small key={d}>{d}</small>)}
        {cells.map((k, i) => (k && days[k] !== undefined
          ? <i key={k} style={{ background: `rgba(${days[k] >= 0 ? '47,227,154' : '255,93,115'},${(0.25 + 0.75 * (Math.abs(days[k]) / top)).toFixed(2)})` }} title={`${k} ${signed(days[k])}`} />
          : <i key={k || `e${i}`} className="e" />))}
      </div>
      <div className="sw-row between"><b className={tone(net)}>{signed(net)}</b><span className="sw-muted">{t('{g} green · {r} red days', { g: green, r: Object.keys(days).length - green })}</span></div>
    </Card>
  );
}

// 9. P&L by setup -------------------------------------------------------------------------------
function Setups({ trades, library = [], style }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState('90d');
  const setupTags = new Set(library.filter((x) => x.kind === 'setup').map((x) => x.name.toLowerCase()));
  const by = {};
  for (const x of since(trades, period)) for (const tag of x.tags || []) if (setupTags.has(tag.toLowerCase())) (by[tag] = by[tag] || []).push(x);
  const rows = Object.entries(by).map(([n, l]) => [n, stats(l)]).sort((p, q) => q[1].net - p[1].net);
  const info = 'Your setups (setup tags and Playbook plays) ranked by what they made: trades, win rate and net P&L.';
  const seg = <Seg options={PERIODS3} value={period} onChange={setPeriod} />;
  if (!rows.length) return <Card title="P&L by setup" info={info} extra={seg}><Empty text="Tag trades with a setup (or put them in a Playbook play) to see which setups pay." /></Card>;
  const top = Math.max(1, ...rows.map(([, s]) => Math.abs(s.net)));
  const worst = rows[rows.length - 1];
  if (style === 'b') {
    return (
      <Card title="P&L by setup" info={info} extra={seg}>
        <ul className="sw-hsplit nw-wide-label">{rows.map(([n, s]) => <li key={n}><span>{n} <small className="sw-muted">{Math.round(s.wr)}%</small></span><span className="tk"><i className={tone(s.net)} style={{ width: `${(Math.abs(s.net) / top) * 50}%`, [s.net < 0 ? 'right' : 'left']: '50%' }} /></span><b className={tone(s.net)}>{short(s.net)}</b></li>)}</ul>
      </Card>
    );
  }
  return (
    <Card title="P&L by setup" info={info} extra={seg}>
      <table className="nw-tbl"><thead><tr><th>{t('Setup')}</th><th>{t('Trades')}</th><th>{t('Win %')}</th><th>{t('Net')}</th></tr></thead>
        <tbody>{rows.map(([n, s]) => <tr key={n}><td><b>{n}</b></td><td className="nw-mono">{s.n}</td><td className="nw-mono">{Math.round(s.wr)}%</td><td className={`nw-mono ${tone(s.net)}`}><b>{signed(s.net)}</b></td></tr>)}</tbody></table>
      <span className="sw-foot">{t('Your best setup: {name}.', { name: rows[0][0] })} {worst[1].net < 0 ? t('Losing money: {name}.', { name: worst[0] }) : t('Every setup made money in this period.')}</span>
    </Card>
  );
}

// 10. Balance & growth (personal accounts) ------------------------------------------------------
function Growth({ ctx, style }) {
  const { t } = useI18n();
  const { account, accounts, allTrades } = ctx;
  const pick = account && !isPropAccount(account) ? [account] : accounts.filter((a) => !a.archived && !isPropAccount(a));
  const info = 'Your real-money account: what you started with, what you deposited and withdrew, and what trading made. Deposits are kept apart, so growth is only what trading earned.';
  if (!pick.length) return <Card title="Balance & growth" info={info}><Empty text="Add a personal account to see how it grows." /></Card>;
  const ids = new Set(pick.map((a) => String(a._id)));
  const start = pick.reduce((s, a) => s + (a.size || 0), 0);
  let dep = 0;
  let wd = 0;
  for (const a of pick) for (const f of a.cashFlows || []) { if (f.kind === 'deposit') dep += f.amount; else if (f.kind === 'withdrawal' || f.kind === 'payout') wd += f.amount; }
  const pl = allTrades.filter((x) => ids.has(String(x.accountId))).reduce((s, x) => s + netPnl(x), 0);
  const now = start + dep - wd + pl;
  const growth = start + dep ? (pl / (start + dep)) * 100 : null;
  const name = pick.length === 1 ? pick[0].name : t('{n} personal accounts', { n: pick.length });
  if (style === 'b') {
    return (
      <Card title="Balance & growth" info={info}>
        <span className="sw-muted">{name}</span><div className="sw-big">{money(now)}</div>
        <div className="sw-kv"><span>{t('Started with')}</span><b>{money(start)}</b></div><div className="sw-kv"><span>{t('Deposited')}</span><b>{signed(dep)}</b></div>
        <div className="sw-kv"><span>{t('Withdrew')}</span><b>{money(-wd)}</b></div><div className="sw-kv"><span>{t('Trading made')}</span><b className={tone(pl)}>{signed(pl)}</b></div>
        {growth !== null && <span className="sw-foot">{t('{p}% growth from trading alone.', { p: `${growth > 0 ? '+' : ''}${growth.toFixed(1)}` })}</span>}
      </Card>
    );
  }
  const steps = [[t('Start'), start, 'm'], [t('Deposits'), dep, 'v'], [t('Withdrawals'), -wd, 'v'], [t('Trading P&L'), pl, pl >= 0 ? 'g' : 'r'], [t('Now'), now, 'c']];
  const peak = Math.max(1, start, start + dep, start + dep - wd + Math.max(0, pl), now) * 1.05;
  let run = 0;
  return (
    <Card title="Balance & growth" info={info} extra={growth !== null && <Chips items={[[t('{p}% from trading', { p: `${growth > 0 ? '+' : ''}${growth.toFixed(1)}` }), growth >= 0 ? 'g' : 'r']]} />}>
      <span className="sw-muted">{name}</span><div className="sw-big">{money(now)}</div>
      <div className="nw-wf">{steps.map(([n, v, c], i) => {
        let lo;
        let hi;
        if (i === 0 || i === 4) { lo = 0; hi = v; if (i === 0) run = v; } else { [lo, hi] = [Math.min(run, run + v), Math.max(run, run + v)]; run += v; }
        return <span key={n} className="nw-wfc"><span className="nw-wfbar"><i className={c} style={{ bottom: `${Math.max(0, lo / peak) * 100}%`, height: `${Math.max(1.5, ((hi - lo) / peak) * 100)}%` }} /></span><small>{n}</small><b className="nw-mono">{i > 0 && i < 4 ? signed(v) : money(v)}</b></span>;
      })}</div>
      <span className="sw-foot">{t('Deposits and withdrawals are kept apart, so growth is only what trading made.')}</span>
    </Card>
  );
}

// 11. Tool costs vs profit ----------------------------------------------------------------------
const PROP_COSTS = new Set(['evaluation', 'reset', 'activation']);
let expensesCache = null;
function ToolCosts({ ctx, style }) {
  const { t } = useI18n();
  const [list, setList] = useState(expensesCache);
  const [period, setPeriod] = useState('all');
  useEffect(() => {
    let live = true;
    api('/expenses').then((r) => { expensesCache = r || []; if (live) setList(expensesCache); }).catch(() => live && setList([]));
    return () => { live = false; };
  }, []);
  const { account, accounts, allTrades } = ctx;
  const year = currentTradingDay().slice(0, 4);
  const tools = useMemo(() => (list || []).filter((e) => !PROP_COSTS.has(e.category) && !e.accountId && (period === 'all' || String(e.date).startsWith(year))), [list, period, year]);
  const info = 'What you spend on courses, data, platforms and subscriptions against what your real-money accounts made. Prop firm fees (evaluations, resets) are in My Trading Journey → Am I profitable?';
  const seg = <Seg options={[['ytd', 'YTD'], ['all', 'All']]} value={period} onChange={setPeriod} />;
  if (list === null) return <Card title="Tool costs vs profit" info={info} extra={seg}><span className="dw-skeleton" /></Card>;
  const personal = account && !isPropAccount(account) ? new Set([String(account._id)]) : new Set(accounts.filter((a) => !isPropAccount(a)).map((a) => String(a._id)));
  const pl = allTrades.filter((x) => personal.has(String(x.accountId)) && (period === 'all' || tradingDayKey(x.date).startsWith(year))).reduce((s, x) => s + netPnl(x), 0);
  const spent = tools.reduce((s, e) => s + (e.amount || 0), 0);
  if (!tools.length) return <Card title="Tool costs vs profit" info={info} extra={seg}><Empty text="Log what you spend on courses, data and platforms in My Trading Journey to see it against your profit." /></Card>;
  if (style === 'b') {
    const by = {};
    for (const e of tools) by[e.category] = (by[e.category] || 0) + (e.amount || 0);
    const rows = Object.entries(by).sort((p, q) => q[1] - p[1]);
    return (
      <Card title="Where your tool money goes" info={info} extra={seg}>
        <ul className="nw-mlist">{rows.map(([c, v]) => <li key={c}><span>{t(c.charAt(0).toUpperCase() + c.slice(1))}</span><span className="tk"><i className="loss" style={{ width: `${(v / rows[0][1]) * 100}%`, left: 0 }} /></span><b className="loss">{money(-v)}</b></li>)}</ul>
        <span className="sw-foot">{t('Prop firm fees are in My Trading Journey → Am I profitable?')}</span>
      </Card>
    );
  }
  return (
    <Card title="Tool costs vs profit" info={info} extra={seg}>
      <div className={`sw-big ${tone(pl - spent)}`}>{signed(pl - spent)}</div>
      <span className="sw-muted">{t('after {amount} on courses, data and platforms', { amount: money(spent) })}</span>
      <div className="sw-boxes two"><span><small>{t('Trading P&L (real-money accounts)')}</small><b className={tone(pl)}>{signed(pl)}</b></span><span><small>{t('Tools & courses')}</small><b className="loss">{money(-spent)}</b></span></div>
      {pl > 0 && <span className="sw-foot">{t('Tools and courses ate {p}% of your trading profit.', { p: Math.round((spent / pl) * 100) })}</span>}
    </Card>
  );
}

export function NewWidget({ id, style, ctx }) {
  const { trades, notes, library } = ctx;
  switch (id) {
    case 'avgWinLoss': return <AvgWinLoss trades={trades} style={style} />;
    case 'accountsList': return <AccountsList ctx={ctx} style={style} />;
    case 'mom': return <MonthOverMonth trades={trades} style={style} />;
    case 'mistakes': return <MistakesCost trades={trades} library={library} style={style} />;
    case 'discipline': return <Discipline trades={trades} notes={notes} style={style} />;
    case 'afterLoss': return <AfterLoss trades={trades} style={style} />;
    case 'monthly': return <Monthly trades={trades} style={style} />;
    case 'heatmap': return <Heatmap trades={trades} style={style} />;
    case 'setups': return <Setups trades={trades} library={library} style={style} />;
    case 'growth': return <Growth ctx={ctx} style={style} />;
    case 'toolCosts': return <ToolCosts ctx={ctx} style={style} />;
    default: return null;
  }
}
